import { useState } from 'react';
import { TicketLogin, type TicketLang } from '@matix/ui';
import { useAuthStore } from '../../store/authStore';

/**
 * KDS sign-in.
 *
 * Presentation is the shared `TicketLogin` from @matix/ui — the same screen
 * the till and the admin console show. It replaces the app's old inline-styled
 * card, which carried no branding at all.
 *
 * The KDS has no language provider of its own (every other screen is icons
 * and order numbers), so the toggle's choice is kept in local state and
 * persisted per device — enough for the one screen that has prose on it.
 */

const LANG_KEY = 'pos:kds:lang';

function initialLang(): TicketLang {
  if (typeof window === 'undefined') return 'en';
  return window.localStorage.getItem(LANG_KEY) === 'ar' ? 'ar' : 'en';
}

export function LoginScreen({ banner }: { banner: string | null }): JSX.Element {
  const error = useAuthStore((s) => s.error);
  const isSubmitting = useAuthStore((s) => s.state.kind === 'logging-in');
  const login = useAuthStore((s) => s.login);
  const clearError = useAuthStore((s) => s.clearError);

  const [lang, setLangState] = useState<TicketLang>(initialLang);
  const [values, setValues] = useState({
    tenant: '',
    username: '',
    password: '',
    remember: true,
  });

  function setLang(next: TicketLang): void {
    setLangState(next);
    window.localStorage.setItem(LANG_KEY, next);
  }

  async function handleSubmit(): Promise<void> {
    if (isSubmitting) return;
    if (error) clearError();
    await login(values.tenant.trim(), values.username.trim(), values.password);
  }

  return (
    <TicketLogin
      lang={lang}
      onLangChange={setLang}
      console={{
        en: 'Kitchen display · Powered by MatixERP',
        ar: 'شاشة المطبخ · مدعوم من MatixERP',
      }}
      title={{ en: 'Sign in to the kitchen queue', ar: 'تسجيل الدخول إلى طابور المطبخ' }}
      tenantLabel={{ en: 'Company', ar: 'الشركة' }}
      values={values}
      onChange={(patch) => setValues((v) => ({ ...v, ...patch }))}
      onSubmit={() => void handleSubmit()}
      busy={isSubmitting}
      banner={banner}
      error={error}
      showRemember={false}
    />
  );
}
